import type { Dispatch, SetStateAction } from "react";
import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import { canFinanceWrite } from "helpers/financePermissions";
import { useAccess } from "hooks/useAccess";
import {
  baixarTituloPagar,
  baixarTituloReceber,
} from "../services/baixa-titulo.service";
import type {
  BaixaTituloDialogState,
  BaixaTituloForm,
  TipoBaixaTitulo,
} from "../types/baixa-titulo.types";
import type { ContaReceberView } from "../types/contas-receber.types";
import type { ContaPagarView } from "../types/contas-pagar.types";
import type { ContaFinanceiraFiltroApi } from "../types/extrato-financeiro.types";
import { parseValorMonetarioInput } from "../helpers/financeiro-formatters";

function formPadrao(valorAberto: number): BaixaTituloForm {
  return {
    contaFinanceiraId: "",
    dataBaixa: format(new Date(), "yyyy-MM-dd"),
    valorBaixa:
      Number.isFinite(valorAberto) && valorAberto > 0
        ? valorAberto.toFixed(2)
        : "",
    observacao: "",
  };
}

function dialogFechado(): BaixaTituloDialogState {
  return {
    open: false,
    tipo: "RECEBER",
    tituloId: "",
  };
}

function parseValorBaixaInput(raw: string): number {
  return parseValorMonetarioInput(raw);
}

export type BaixaTituloFeedback =
  | { severity: "success"; message: string }
  | null;

export type UseBaixaTituloOptions = {
  onSuccess?: () => void;
  contasFinanceiras?: ContaFinanceiraFiltroApi[];
};

export type UseBaixaTituloResult = {
  podeBaixaManual: boolean;
  dialog: BaixaTituloDialogState;
  form: BaixaTituloForm;
  setForm: Dispatch<SetStateAction<BaixaTituloForm>>;
  loading: boolean;
  error: string | null;
  feedback: BaixaTituloFeedback;
  clearFeedback: () => void;
  abrirBaixaReceber: (titulo: ContaReceberView) => void;
  abrirBaixaPagar: (
    titulo: ContaPagarView,
    contasFinanceiras?: ContaFinanceiraFiltroApi[],
  ) => void;
  fecharDialog: () => void;
  confirmarBaixa: () => Promise<void>;
};

export function useBaixaTitulo(
  options: UseBaixaTituloOptions = {},
): UseBaixaTituloResult {
  const { onSuccess, contasFinanceiras = [] } = options;
  const access = useAccess();

  const podeBaixaManual = useMemo(
    () => canFinanceWrite(access),
    [access]
  );

  const [dialog, setDialog] = useState<BaixaTituloDialogState>(() =>
    dialogFechado(),
  );
  const [form, setForm] = useState<BaixaTituloForm>(() => formPadrao(0));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<BaixaTituloFeedback>(null);

  const clearFeedback = useCallback(() => {
    setFeedback(null);
  }, []);

  const fecharDialog = useCallback(() => {
    setDialog(dialogFechado());
    setError(null);
    setLoading(false);
  }, []);

  const abrirBaixaReceber = useCallback((titulo: ContaReceberView) => {
    setFeedback(null);
    setError(null);
    setDialog({
      open: true,
      tipo: "RECEBER",
      tituloId: titulo.id,
      tituloDescricao: titulo.descricao,
      tituloDetalhe: titulo.clienteNome,
      valorAberto: titulo.valorAberto,
      dataVencimento: titulo.dataVencimento,
    });
    setForm(formPadrao(titulo.valorAberto));
  }, []);

  const abrirBaixaPagar = useCallback(
    (titulo: ContaPagarView, contasFinanceiras?: ContaFinanceiraFiltroApi[]) => {
      setFeedback(null);
      setError(null);
      const padrao =
        contasFinanceiras?.find((c) => c.ehPadrao) ?? contasFinanceiras?.[0];
      const base = formPadrao(titulo.valorAberto);
      setDialog({
        open: true,
        tipo: "PAGAR",
        tituloId: titulo.id,
        tituloDescricao: titulo.descricao,
        tituloDetalhe: titulo.fornecedorNome,
        valorAberto: titulo.valorAberto,
        dataVencimento: titulo.dataVencimento,
      });
      setForm({
        ...base,
        contaFinanceiraId: padrao?.id ?? "",
      });
    },
    [],
  );

  const confirmarBaixa = useCallback(async () => {
    if (!dialog.open || !dialog.tituloId) return;

    setError(null);
    const contaId = form.contaFinanceiraId.trim();
    if (!contaId) {
      setError("Selecione a conta financeira.");
      return;
    }
    if (!form.dataBaixa?.trim()) {
      setError("Informe a data da baixa.");
      return;
    }

    const valorNum =
      dialog.valorAberto != null &&
      Number.isFinite(dialog.valorAberto) &&
      dialog.valorAberto > 0
        ? dialog.valorAberto
        : parseValorBaixaInput(form.valorBaixa);
    if (!Number.isFinite(valorNum) || valorNum <= 0) {
      setError("Valor da baixa deve ser maior que zero.");
      return;
    }

    const aberto = dialog.valorAberto;
    if (
      aberto != null &&
      Number.isFinite(aberto) &&
      Math.abs(valorNum - aberto) > 0.009
    ) {
      setError("A baixa deve ser integral, igual ao valor em aberto.");
      return;
    }

    if (dialog.tipo === "PAGAR") {
      const conta = contasFinanceiras.find((c) => c.id === contaId);
      const saldo =
        conta?.saldoAtual != null && Number.isFinite(conta.saldoAtual)
          ? conta.saldoAtual
          : null;
      if (saldo != null && valorNum > saldo + 0.009) {
        setError(
          `Saldo insuficiente na conta. Disponível: R$ ${saldo.toFixed(2).replace(".", ",")}.`,
        );
        return;
      }
    }

    const observacao =
      form.observacao?.trim() !== "" ? form.observacao.trim() : undefined;

    setLoading(true);

    try {
      const tipo: TipoBaixaTitulo = dialog.tipo;
      if (tipo === "RECEBER") {
        const { data, status, error: err } = await baixarTituloReceber(
          dialog.tituloId,
          {
            contaFinanceiraId: contaId,
            dataRecebimento: form.dataBaixa.trim(),
            valorRecebido: valorNum,
            observacao,
          },
        );
        if (status === 200 && data?.ok) {
          fecharDialog();
          setFeedback({
            severity: "success",
            message: data.mensagem ?? "Baixa registrada com sucesso.",
          });
          onSuccess?.();
          return;
        }
        setError(err ?? data?.mensagem ?? "Não foi possível baixar o título.");
        return;
      }

      const { data, status, error: err } = await baixarTituloPagar(
        dialog.tituloId,
        {
          contaFinanceiraId: contaId,
          dataPagamento: form.dataBaixa.trim(),
          valorPago: valorNum,
          observacao,
        },
      );
      if (status === 200 && data?.ok) {
        fecharDialog();
        setFeedback({
          severity: "success",
          message: data.mensagem ?? "Baixa registrada com sucesso.",
        });
        onSuccess?.();
        return;
      }
      setError(err ?? data?.mensagem ?? "Não foi possível baixar o título.");
    } finally {
      setLoading(false);
    }
  }, [dialog, form, fecharDialog, onSuccess, contasFinanceiras]);

  return {
    podeBaixaManual,
    dialog,
    form,
    setForm,
    loading,
    error,
    feedback,
    clearFeedback,
    abrirBaixaReceber,
    abrirBaixaPagar,
    fecharDialog,
    confirmarBaixa,
  };
}
